import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "./api";

const AuthContext = createContext(null);

const normalizeSession = (user) => user ? ({
  user: {
    ...user,
    name: user.displayName || user.name || user.username,
    role: user.roles?.[0]?.name || user.role || "Restricted User",
    mustChangePassword: false,
  },
}) : null;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [authState, setAuthState] = useState("loading");
  const [authError, setAuthError] = useState("");

  const restoreSession = useCallback(async () => {
    setAuthState("loading");
    setAuthError("");
    try {
      const result = await apiFetch("/auth/session");
      setSession(normalizeSession(result?.user));
      setAuthState("authenticated");
      return { ok: true, user: result?.user };
    } catch (error) {
      setSession(null);
      if (error instanceof ApiError && error.status === 401) {
        setAuthState("unauthenticated");
        return { ok: false, error: error.message, code: error.code };
      }
      setAuthState("error");
      setAuthError(error.message || "Session verification failed.");
      return { ok: false, error: error.message || "Session verification failed.", code: error.code };
    }
  }, []);

  useEffect(() => { restoreSession(); }, [restoreSession]);
  useEffect(() => {
    const expired = (event) => {
      setSession(null);
      setAuthState("unauthenticated");
      setAuthError(event.detail?.message || "Your session expired. Please sign in again.");
    };
    window.addEventListener("hse:session-expired", expired);
    return () => window.removeEventListener("hse:session-expired", expired);
  }, []);

  const login = async (username, password) => {
    setAuthError("");
    try {
      await apiFetch("/auth/login", { method: "POST", body: { username, password } });
      return await restoreSession();
    } catch (error) {
      setSession(null);
      setAuthState("unauthenticated");
      return { ok: false, error: error.message || "Invalid username or password.", code: error.code };
    }
  };

  const logout = async () => {
    try { await apiFetch("/auth/logout", { method: "POST" }); } catch {}
    setSession(null);
    setAuthState("unauthenticated");
  };

  const updateCredentials = async ({ currentPassword, newUsername, newPassword, displayName }) => {
    try {
      await apiFetch("/auth/credentials", {
        method: "PATCH",
        body: { currentPassword, newUsername, newPassword, displayName },
      });
      await restoreSession();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message || "Credential update failed.", code: error.code };
    }
  };

  const creds = useMemo(() => session ? ({
    username: session.user.username,
    displayName: session.user.displayName || session.user.name,
    role: session.user.role,
  }) : null, [session]);

  return (
    <AuthContext.Provider value={{
      session,
      authState,
      authError,
      isAuthed: authState === "authenticated" && Boolean(session),
      login,
      logout,
      restoreSession,
      updateCredentials,
      creds,
      hasPermission: (permission) => session?.user?.permissions?.includes(permission) === true,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be inside AuthProvider");
  return context;
}
